import { useId } from 'react';
import {
  OFFLINE_STYLE,
  isEntityUnavailable,
  styleForAction,
  thermostatAction,
  useThermostat,
} from './thermostatState';

// Kitchen kiosk thermostat widget. Reads /api/ha?entities=climate.thermostat
// (the HA proxy), which includes current temperature, the setpoints, and the
// HVAC mode as attributes. (No humidity: the Nest's Matter firmware doesn't
// expose its humidity sensor — the old ecobee's small humidity line was
// removed 2026-09 when the Nest replaced it.)
//
// Visual: a 270° speedometer arc whose color reflects the current hvac_action
// (rose=heating, blue=cooling, amber=fan/drying, neutral=idle/off). The arc
// is dashed and animated via stroke-dashoffset so the segments appear to
// flow around it — combined with an SVG glow filter, it reads as glowing
// fluid moving along the dial. Low and high setpoints sit as labels under
// the lower-left and lower-right ends of the arc. Current temperature is
// the dominant centered number.

// Geometry constants for the speedometer SVG.
const SIZE = 220;
const STROKE = 14;
const PADDING = 4;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R = (SIZE - STROKE) / 2 - PADDING;
// Angles use a "compass" convention: 0° = up (12 o'clock), clockwise positive.
// Arc starts at lower-left (225°) and sweeps 270° clockwise through the top
// (360°) to lower-right (495° ≡ 135°). The bottom 90° is the gap.
const ARC_START_DEG = 225;
const ARC_SWEEP_DEG = 270;
const ARC_END_DEG = ARC_START_DEG + ARC_SWEEP_DEG;

// Temperature scale: 0°F is anchored at the arc start (lower-left, 225°)
// and 100°F at the arc end (lower-right, 495° ≡ 135°). The full 270° sweep
// covers the 0–100°F range, so 50°F lands at the top (12 o'clock) and
// typical indoor temps sit in the upper-right quadrant.
const TEMP_AT_START = 0;
const TEMP_AT_END = 100;
const DEG_PER_F = (ARC_END_DEG - ARC_START_DEG) / (TEMP_AT_END - TEMP_AT_START);
function tempToAngle(temp: number): number {
  return ARC_START_DEG + (temp - TEMP_AT_START) * DEG_PER_F;
}

// Temperature gradient — indigo → cyan → amber → orange → red — sampled
// across the arc. SVG's <linearGradient> projects along a straight line,
// so on a 270° curve the colors don't track arc angle. Instead, slice the
// arc into many short segments, each filled with a color blended at its
// midpoint along the temperature scale.
//
// Colors come from the project's design tokens via `color-mix()`, so the
// gradient inherits any palette changes for free — no hard-coded hex
// values. Stops are evenly spaced at 0, 25, 50, 75, 100% of the scale.
const COLOR_STOPS: Array<{ pos: number; cssVar: string }> = [
  { pos: 0.0, cssVar: '--berg-indigo' },
  { pos: 1.0, cssVar: '--berg-red' },
];

function gradientColorAt(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  for (let i = 0; i < COLOR_STOPS.length - 1; i++) {
    const a = COLOR_STOPS[i];
    const b = COLOR_STOPS[i + 1];
    if (clamped <= b.pos) {
      const localT = (clamped - a.pos) / (b.pos - a.pos);
      const aPct = ((1 - localT) * 100).toFixed(1);
      return `color-mix(in srgb, var(${a.cssVar}) ${aPct}%, var(${b.cssVar}))`;
    }
  }
  return `var(${COLOR_STOPS[COLOR_STOPS.length - 1].cssVar})`;
}

// Dial is rendered as evenly-spaced radial tick marks (hashes) along the
// 270° arc rather than a continuous band. 41 ticks divide the 0–100°F scale
// into 40 equal steps of 2.5°F each, all the same length and width.
const TICK_COUNT = 41;
const TICK_LEN = 16;
const TICK_WIDTH = 2;

interface ArcTick {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
}
const ARC_TICKS: ArcTick[] = (() => {
  const ticks: ArcTick[] = [];
  const halfLen = TICK_LEN / 2;
  for (let i = 0; i < TICK_COUNT; i++) {
    const t = i / (TICK_COUNT - 1);
    const angle = ARC_START_DEG + t * ARC_SWEEP_DEG;
    const inner = polar(angle, R - halfLen);
    const outer = polar(angle, R + halfLen);
    ticks.push({
      x1: inner.x,
      y1: inner.y,
      x2: outer.x,
      y2: outer.y,
      color: gradientColorAt(t),
    });
  }
  return ticks;
})();


// Compass-angle → SVG cartesian. 0° = up, angles increase clockwise.
function polar(angleDeg: number, radius: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + radius * Math.sin(rad),
    y: CY - radius * Math.cos(rad),
  };
}

interface EcobeeWidgetProps {
  className?: string;
}

export function EcobeeWidget({ className }: EcobeeWidgetProps = {}) {
  const { thermostat, error } = useThermostat();
  // Unique filter id per mount so multiple instances on a page don't share
  // the same `glow` defs. Must be called unconditionally — keep above the
  // early-return below to comply with the Rules of Hooks.
  const reactId = useId();
  const filterId = `kiosk-arc-glow-${reactId.replace(/:/g, '')}`;

  if (!thermostat) {
    return (
      <div className={`card text-sm text-fg-secondary ${className ?? ''}`}>
        {error ?? 'Loading thermostat…'}
      </div>
    );
  }

  const attrs = thermostat.attributes;
  const currentTemp = attrs.current_temperature;
  const action = isEntityUnavailable(thermostat.state)
    ? OFFLINE_STYLE
    : styleForAction(thermostatAction(thermostat));

  const ringColor = `var(${action.colorVar})`;
  const ModeIcon = action.Icon;

  // Current-temp dot position (centered on the arc stroke). Clamped to
  // the visible 0–100°F range so an out-of-scale reading still lands on
  // the dial rather than floating off in space.
  const currentDot =
    typeof currentTemp === 'number'
      ? polar(tempToAngle(Math.max(TEMP_AT_START, Math.min(TEMP_AT_END, currentTemp))), R)
      : null;

  return (
    <div
      className={`card relative flex flex-col items-stretch gap-3 overflow-hidden ${className ?? ''}`}
      style={
        action.softVar
          ? {
              backgroundImage: `radial-gradient(circle at top left, var(${action.softVar}), transparent 80%)`,
            }
          : undefined
      }
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[0.625rem] uppercase tracking-widest text-fg-secondary">
          Thermostat
        </span>
        <span
          className="inline-flex items-center rounded-full border px-2.5 py-1 text-[0.625rem] uppercase tracking-widest"
          style={{ color: ringColor, borderColor: ringColor }}
        >
          {action.label}
        </span>
      </div>

      <div className="flex items-center justify-center pt-6 -mb-2">
        <div className="relative aspect-square w-full max-w-[240px]">
          <svg
            viewBox={`0 0 ${SIZE} ${SIZE}`}
            className="absolute inset-0 w-full h-full overflow-visible"
            aria-hidden="true"
          >
            <defs>
              <filter id={filterId} x="-30%" y="-30%" width="160%" height="160%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>
            {/* Tick-mark dial: evenly-spaced radial hashes along the 270°
                arc, each colored at its position on the indigo→red
                temperature gradient. The glow filter softens the whole
                group into a halo. */}
            <g
              filter={`url(#${filterId})`}
              opacity={action.active ? 1 : 0.35}
            >
              {ARC_TICKS.map((tick, i) => (
                <line
                  key={i}
                  x1={tick.x1}
                  y1={tick.y1}
                  x2={tick.x2}
                  y2={tick.y2}
                  stroke={tick.color}
                  strokeWidth={TICK_WIDTH}
                  strokeLinecap="round"
                />
              ))}
            </g>
            {/* Current-temperature dot: filled marker centered on the arc
                at the angle for the live indoor reading. */}
            {currentDot && (
              <circle
                cx={currentDot.x}
                cy={currentDot.y}
                r={5}
                fill="var(--berg-text)"
              />
            )}
          </svg>

          {/* Center content sits inside the dial, on top of the SVG. */}
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <span className="text-7xl font-light tabular tracking-tight leading-none">
              {typeof currentTemp === 'number' ? Math.round(currentTemp) : '–'}
            </span>
          </div>
        </div>
      </div>

      {/* Mode glyph in the dial's bottom gap (heating / cooling / fan / …).
          Absolutely positioned so it adds no height to the card. */}
      <ModeIcon
        size={36}
        weight="duotone"
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
        style={{ color: ringColor }}
        aria-label={action.label}
      />
    </div>
  );
}
